import React, { useState } from 'react';
import classNames from 'classnames';
import styles from '../assets/invoice-grid.module.scss';
import { Paginated } from 'src/services/api-handlers/pagination';
import Grid, { Column } from 'src/components/react-table/Grid';
import textFilter from 'src/components/react-table/filters/text-filter';
import { QueryParams } from 'src/types/grid';
import { IInvoice } from 'src/types/invoice';
import { CellProps } from 'react-table';
import { Link, useRouteMatch } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import dateTimeFilter from 'src/components/react-table/filters/datetime-filter';
import dropdownFilter from 'src/components/react-table/filters/dropdown-filter';
import OrderEnum from 'src/enumerables/order-enum';
import GeneralEnum from 'src/enumerables/general-enum';
import { getValFromEnum } from 'src/components/redux-form/utils/dropdown-values';
import CaseEnum from 'src/enumerables/case-enum';
import { IServiceType } from 'src/types/service-types';
import ReadyDoeInvoice from './ReadyDoeInvoice';
import StatusInvoiceAction from '../../../../Providers/screens/ProviderDetail/tabs/Invoice/components/StatusInvoiceAction';
import dateFilterMulti from '../../../../../../../components/react-table/filters/date-filter-multi';
import { changeToZeroMoment } from '../../../../../../../helpers/timeHelpers';
import { numberWithCommas } from '../../../../../../../helpers/format';
import SendToDocusignInvoice from './SendToDocusignInvoice';
import InvoiceActionsColumn from './InvoiceActionsColumn';
import EditNoteInvoice from './EditNoteInvoice';
import { IAgencyInfo } from '../../../../../../../types/app';

interface Props {
    data: Paginated<Partial<IInvoice>>;
    filters: any;
    students: SelectObject[];
    vendors: SelectObject[];
    providers: SelectObject[];
    districts: SelectObject[];
    serviceTypes: Paginated<Partial<IServiceType>>;
    getData: (params: QueryParams) => void;
    getDataCsv: (params: QueryParams) => void;
    inProgressCSV: boolean;
    agencyInfo: IAgencyInfo | null;
    persistInvoice: (params: any, arr: any, res: any) => void;
}
const typeEnum = OrderEnum ? OrderEnum.getEnum('OrderType') : {};
const orderTypes: SelectObject[] = getValFromEnum(typeEnum);
const statusEnum = GeneralEnum ? GeneralEnum.getEnum('InvoiceStatus') : {};

const statuses: SelectObject[] = getValFromEnum(statusEnum);

const isSentViaPolarisFilterValues: SelectObject[] = [
    {
        label: 'Yes',
        value: '1',
    },
    {
        label: 'No',
        value: '0',
    },
];

const InvoicesGrid: React.FunctionComponent<Props> = ({
    data,
    getData,
    getDataCsv,
    inProgressCSV,
    students,
    providers,
    persistInvoice,
    districts,
    vendors,
    filters,
    serviceTypes,
    agencyInfo,
}: Props) => {
    const [columns, setColumns] = useState<any>([]);
    const caseEnum = CaseEnum ? CaseEnum.getEnum('caseType') : {};
    const caseTypes: SelectObject[] = getValFromEnum(caseEnum);

    const [invoiceNotes, setInvoiceNotes] = useState([]);

    const serviceTypesVal: any = [];
    serviceTypes.list.forEach((st: Partial<IServiceType>) => {
        serviceTypesVal.push({ value: st.id, label: st.title ? st.title : st.cpt });
    });

    const getColumns = (
        studentsData: SelectObject[],
        districtsData: any,
        vendorsData: any,
        serviceTypesData: any,
        providersData: any,
        agencyInfo: any,
    ): Column<Partial<IInvoice>>[] => [
        {
            Header: '#',
            accessor: 'id',
            disableSortBy: true,
            disableFilters: false,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                if (!original?.provider?.id) {
                    return original.id;
                }
                return (
                    <Link
                        className={'btn btn-info'}
                        to={Router.generate(routes.PROVIDER_DETAIL, {
                            id: original?.provider?.id,
                            tab: 'invoices-detail',
                            subId: original.id,
                        })}
                    >
                        {original.id}
                    </Link>
                );
            },
            Filter: textFilter<IInvoice>(),
        },

        {
            disableSortBy: true,
            Header: 'Date',
            accessor: 'createdAt',

            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                const value: any = original.createdAt;
                return value ? `${changeToZeroMoment(value, 'MMM D, YYYY')}` : '';
            },
            Filter: dateTimeFilter<IInvoice>(),
        },

        {
            disableSortBy: true,
            Header: 'Total (Paid)',
            accessor: 'invoiceTotal',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) =>
                original.invoiceTotal ? (
                    <div>
                        ${numberWithCommas(original.invoiceTotal)}
                        <br />
                        <span className={classNames(styles.fontSmall)}>${numberWithCommas(original.actualTotal)}</span>
                    </div>
                ) : (
                    '$0.00'
                ),
            Filter: textFilter<IInvoice>('number'),
        },
        {
            Header: 'Case #',
            disableSortBy: true,
            accessor: 'occasion.orderEntity.caseNumber',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => (
                <Link
                    to={Router.generate(routes.CASE_EDIT, {
                        id: original?.occasion?.id,
                    })}
                >
                    {original.occasion?.caseNumber}
                </Link>
            ),
            Filter: textFilter<IInvoice>(),
        },
        {
            disableSortBy: true,
            Header: 'Provider',
            accessor: 'provider.id',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                if (!original?.provider) return '';

                return (
                    <Link
                        to={Router.generate(routes.PROVIDER_DETAIL, {
                            id: original?.provider?.id,
                            tab: 'main-info',
                            subId: '',
                        })}
                    >
                        {original.provider?.fullName}
                    </Link>
                );
            },
            Filter: dropdownFilter<IInvoice>(providersData),
        },
        {
            Header: 'Student',
            disableSortBy: true,
            accessor: 'student.id',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                if (!original?.student) return '';

                return (
                    <Link
                        to={Router.generate(routes.STUDENT_DETAIL, {
                            id: original?.student?.id,
                            tab: 'student-info',
                        })}
                    >
                        {`${original.student?.firstName} ${original.student?.lastName}`}
                    </Link>
                );
            },
            Filter: dropdownFilter<IInvoice>(studentsData),
        },
        {
            Header: 'Vendor',
            disableSortBy: true,
            accessor: 'vendor.id',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                if (!original?.vendor) return '';

                return (
                    <Link
                        to={Router.generate(routes.VENDOR_DETAIL, {
                            id: original?.vendor?.id,
                        })}
                    >
                        {`${original.vendor?.name}`}
                    </Link>
                );
            },
            Filter: dropdownFilter<IInvoice>(vendorsData),
        },
        {
            Header: 'District',
            disableSortBy: true,
            accessor: 'district.id',

            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                if (!original?.district) return '';

                return (
                    <Link
                        to={Router.generate(routes.DISTRICT_DETAIL, {
                            id: original?.district?.id,
                        })}
                    >
                        {`${original.district?.name}`}
                    </Link>
                );
            },
            Filter: dropdownFilter<IInvoice>(districtsData),
        },
        {
            Header: 'Service',
            disableSortBy: true,
            accessor: 'occasion.serviceType.id',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                let title = original.occasion?.serviceType?.title;
                if (!title) {
                    title = original.occasion?.serviceType?.cpt;
                }

                return <div>{title}</div>;
            },

            Filter: dropdownFilter<IInvoice>(serviceTypesData),
        },

        {
            Header: 'Period',
            accessor: 'periodStart',
            disableSortBy: true,

            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                const valueOrderType = original.periodStart;
                const monthStart = changeToZeroMoment(valueOrderType, 'M');
                const monthEnd = changeToZeroMoment(original.periodEnd, 'M');
                const YearStart = changeToZeroMoment(valueOrderType, 'YYYY');
                const YearEnd = changeToZeroMoment(original.periodEnd, 'YYYY');
                let yearDate = changeToZeroMoment(valueOrderType, 'YYYY');
                if (YearStart !== YearEnd) {
                    yearDate = `${changeToZeroMoment(valueOrderType, 'YYYY')}-${changeToZeroMoment(
                        valueOrderType,
                        'YY',
                    )}`;
                }
                if (monthStart === monthEnd) {
                    return valueOrderType ? (
                        <div className={'text-center'}>{`${changeToZeroMoment(valueOrderType, 'MMM D, YYYY')}`}</div>
                    ) : (
                        ''
                    );
                } else {
                    return valueOrderType ? (
                        <div className={'text-center'}>{`${changeToZeroMoment(
                            valueOrderType,
                            'MMM',
                        )} ${changeToZeroMoment(original.periodEnd, 'MMM')} - ${changeToZeroMoment(
                            valueOrderType,
                            ' D, ',
                        )} ${yearDate}`}</div>
                    ) : (
                        ''
                    );
                }
            },
            headerClassName: 'text-center w-100 d-block',
            Filter: dateFilterMulti<IInvoice>(),
        },
        {
            Header: 'Type',
            accessor: 'occasion.caseType',
            disableSortBy: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                const valueCaseType = caseTypes.find((it) => +it.value === original.occasion?.caseType);
                return valueCaseType ? valueCaseType.label : '';
            },
            Filter: dropdownFilter<IInvoice>(caseTypes),
        },
        {
            Header: 'Order',
            accessor: 'occasion.orderType',
            disableSortBy: true,
            width: '15%',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                const valueOrderType = orderTypes.find((it) => it.value === original.occasion?.orderType);
                if (!(original?.occasion?.orderEntity && original?.occasion?.orderEntity.id))
                    return valueOrderType ? valueOrderType.label : '';

                return (
                    <Link
                        key={`order${valueOrderType ? valueOrderType.label : ''}`}
                        className={'mr-1'}
                        style={{ borderBottom: '1px solid #d1d1d1', paddingBottom: 1 }}
                        to={Router.generate(routes.ORDER_DETAIL, {
                            id: original?.occasion?.orderEntity?.id,
                        })}
                    >
                        {`${valueOrderType ? valueOrderType.label : ''}`}
                    </Link>
                );
            },
            Filter: dropdownFilter<IInvoice>(orderTypes),
        },
        {
            Header: 'Payment',
            accessor: 'payment',
            disableSortBy: true,
            disableFilters: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                let paymentIds: number[] = [];
                original.paymentInvoices.forEach((it: any) => {
                    paymentIds.push(+it?.payment.id);
                });
                const newSet: any = new Set(paymentIds);
                paymentIds = [...newSet];
                return paymentIds.map((it: any) => {
                    if (it?.status && +it.status === 2) {
                        return null;
                    }

                    return (
                        <Link
                            key={`it${it}`}
                            className={'mr-1'}
                            style={{ borderBottom: '1px solid #d1d1d1', paddingBottom: 1 }}
                            to={Router.generate(routes.PAYMENT_DETAIL, {
                                id: it,
                            })}
                        >
                            {`${it}`}
                        </Link>
                    );
                });
            },
            Filter: textFilter<IInvoice>(),
        },
        {
            Header: 'DOE?',
            accessor: 'isReadyToSent',
            disableSortBy: true,
            disableFilters: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                if (original?.occasion?.orderType && +original.status === 3 && +original?.occasion.orderType === 4) {
                    return <ReadyDoeInvoice persistInvoice={persistInvoice} filters={filters} original={original} />;
                }

                return '';
            },
            Filter: textFilter<IInvoice>(),
        },
        {
            Header: 'DocuSign',
            accessor: 'statusDocusign',
            disableSortBy: true,
            disableFilters: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                if (
                    original.statusDocusign != 0 &&
                    original?.occasion?.caseType &&
                    +original.status === 1 &&
                    (+original?.occasion.caseType === 1 || +original?.occasion.caseType === 5)
                ) {
                    return (
                        <SendToDocusignInvoice persistInvoice={persistInvoice} filters={filters} original={original} />
                    );
                }

                return '';
            },
            Filter: textFilter<IInvoice>(),
        },
        {
            Header: 'Via Polaris?',
            disableSortBy: true,
            accessor: 'occasion.doeEmail',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                if (!original.occasion) {
                    return '';
                }
                if (!agencyInfo?.isSentAllInvoiceToDoe) {
                    return original.occasion?.doeEmail ? 'No' : 'Yes';
                }
                return 'No';
            },
            Filter: dropdownFilter<IInvoice>(isSentViaPolarisFilterValues, styles.minWidth),
        },
        {
            Header: 'Status',
            accessor: 'status',
            width: '15%',
            disableSortBy: true,
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                if (original.totalRow) {
                    return '';
                }
                return <StatusInvoiceAction data={original} />;
            },
            Filter: dropdownFilter<IInvoice>(statuses, styles.minWidth),
        },
        {
            Header: 'Note',
            disableSortBy: true,
            accessor: 'note',
            Cell: ({
                cell: {
                    row: { original },
                },
            }: CellProps<IInvoice>) => {
                return (
                    <EditNoteInvoice
                        setInvoiceNotes={setInvoiceNotes}
                        invoiceNotes={invoiceNotes}
                        original={original}
                    />
                );
            },
            Filter: textFilter<IInvoice>(),
        },
        {
            Header: 'Actions',
            accessor: 'updatedAt',
            Cell: InvoiceActionsColumn,
            disableSortBy: true,
            disableFilters: true,
            width: '10%',
        },
    ];

    if (!columns.length && students.length && providers.length && agencyInfo) {
        setColumns(getColumns(students, districts, vendors, serviceTypesVal, providers, agencyInfo));
    }

    const match: any = useRouteMatch();
    const filtersInit = [];
    if (Object.values(match.params).length && match.params.status) {
        Object.keys(match.params).forEach((k) => {
            filtersInit.push({ id: k, value: +match.params[k] });
        });
    }
    if (filtersInit.length) {
        filtersInit.push({ id: 'occasion.caseType', value: 1 });
    }
    return (
        <div className={classNames(styles.grid)}>
            <Grid<Partial<IInvoice>>
                getDataCsv={getDataCsv}
                inProgressCSV={inProgressCSV}
                filtersInit={filtersInit}
                columns={columns}
                data={data}
                getData={getData}
            />
        </div>
    );
};

export default InvoicesGrid;
